import ArrowRight from '../../icons/ArrowRight';
import './ToolsDashboard.css'
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import useLanguagePrefix from '../../LanguagePrefix';

function ToolsDashboard() {
    const langPrefix = useLanguagePrefix();
    const { t } = useTranslation();

    return (
        <div>
            <Helmet>

                <title>Free Online Calculators for Hotels, Resorts & Hospitality Operators - Prostay</title>
                <meta name="description" content="Access free online calculators for hotels, resorts, and hospitality operators. Use tools like RevPAR, ADR, Occupancy Rate, and more to make smarter revenue and cost decisions." />
                <link rel="canonical" href={"https://www.prostay.com" + langPrefix + "/tools/"} />

                <link rel="alternate" href="https://www.prostay.com/en/tools/" />
                <link rel="alternate" href="https://www.prostay.com/es/tools/" />

                <meta property="og:title" content="Free Online Calculators for Hotels, Resorts & Hospitality Operators - Prostay" />
                <meta property="og:description" content="Access free online calculators for hotels, resorts, and hospitality operators. Use tools like RevPAR, ADR, Occupancy Rate, and more to make smarter revenue and cost decisions." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={"https://www.prostay.com" + langPrefix + "/tools/"} />
                <meta property="og:image" content='https://prostay.com/images/ogImage.png' />

                <meta property="og:site_name" content="Prostay" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Free Online Calculators for Hotels, Resorts & Hospitality Operators - Prostay" />
                <meta name="twitter:description" content="Access free online calculators for hotels, resorts, and hospitality operators. Use tools like RevPAR, ADR, Occupancy Rate, and more to make smarter revenue and cost decisions." />
                <meta name="twitter:image" content='https://prostay.com/images/ogImage.png' />
                <meta name="twitter:site" content="@prostaypms" />
                <meta name="twitter:creator" content="@prostaypms" />

            </Helmet>
            <div className='tools-dashboard'>
                <div className='d-flex align-items-center flex-column page-content text-center'>
                    <h1 className='mb-3' style={{ maxWidth: '1000px' }}>
                        {t('Free Online calculators for Hotels, Resorts, and Hospitality Operators')}
                    </h1>
                    <p>
                        {t('Prostay’s online calculator for hotels puts a full revenue toolkit at your fingertips—simply enter rate and occupancy data to see instant ADR, RevPAR, Occupancy Rate, GOPPAR, TRevPAR, and more. These real‑time metrics turn guesswork into data‑driven pricing, sharper performance benchmarks, and accurate demand forecasts, so you can fine‑tune strategy and boost profit without spreadsheets or complicated formulas.')}
                    </p>
                </div>
                <div className='tools-grid'>
                    <div className='rounded-white-box box d-flex flex-column'>
                        <h3 style={{ fontSize: 20 }}>
                            {t('Average Daily Rate (ADR)')}
                        </h3>
                        <small className='label-xsmall'>
                            {t('Average Daily Rate (ADR) is a core hotel metric used to calculate the average revenue earned per sold room per day.')}
                        </small>
                        <a className='label-xsmall mt-2' href={`${langPrefix}/adr-calculator/`}>
                            {t('View')} &gt;
                        </a>
                    </div>
                    <div className='rounded-white-box box d-flex flex-column'>
                        <h3 style={{ fontSize: 20 }}>
                            {t('RevPAR')}
                        </h3>
                        <small className='label-xsmall'>
                            {t('Revenue per Available Room (RevPAR) shows how effectively your hotel fills rooms at profitable rates.')}
                        </small>
                        <a className='label-xsmall mt-2' href={`${langPrefix}/revpar-calculator/`}>
                            {t('View')} &gt;
                        </a>
                    </div>
                    <div className='rounded-white-box box d-flex flex-column'>
                        <h3 style={{ fontSize: 20 }}>
                            {t('Occupancy Rate')}
                        </h3>
                        <small className='label-xsmall'>
                            {t('Occupancy Rate measures the percentage of available rooms that are sold during a given period.')}
                        </small>
                        <a className='label-xsmall mt-2' href={`${langPrefix}/occupancy-rate/`}>
                            {t('View')} &gt;
                        </a>
                    </div>
                    <div className='rounded-white-box box d-flex flex-column'>
                        <h3 style={{ fontSize: 20 }}>
                            {t('GOPPAR')}
                        </h3>
                        <small className='label-xsmall'>
                            {t('Gross Operating Profit per Available Room (GOPPAR) evaluates how efficiently a hotel converts revenue into profit.')}
                        </small>
                        <a className='label-xsmall mt-2' href={`${langPrefix}/goppar-calculator/`}>
                            {t('View')} &gt;
                        </a>
                    </div>
                    <div className='rounded-white-box box d-flex flex-column'>
                        <h3 style={{ fontSize: 20 }}>
                            {t('TrevPAR')}
                        </h3>
                        <small className='label-xsmall'>
                            {t('Total Revenue per Available Room (TRevPAR) expands on RevPAR by including all revenue sources.')}
                        </small>
                        <a className='label-xsmall mt-2' href={`${langPrefix}/trevpar-calculator/`}>
                            {t('View')} &gt;
                        </a>
                    </div>
                    <div className='rounded-white-box box d-flex flex-column'>
                        <h3 style={{ fontSize: 20 }}>
                            {t('TrevPAB')}
                        </h3>
                        <small className='label-xsmall'>
                            {t('Total Revenue per Available Bed (TRevPAB) measures total income generated per bed rather than per room.')}
                        </small>
                        <a className='label-xsmall mt-2' href={`${langPrefix}/trevpab-calculator/`}>
                            {t('View')} &gt;
                        </a>
                    </div>
                    <div className='rounded-white-box box d-flex flex-column'>
                        <h3 style={{ fontSize: 20 }}>
                            {t('Cost Per Occupied Room')}
                        </h3>
                        <small className='label-xsmall'>
                            {t('Cost Per Occupied Room (CPOR) measures the average cost to service one occupied room.')}
                        </small>
                        <a className='label-xsmall mt-2' href={`${langPrefix}/cpor-calculator/`}>
                            {t('View')} &gt;
                        </a>
                    </div>
                    <div className='rounded-white-box box d-flex flex-column'>
                        <h3 style={{ fontSize: 20 }}>
                            {t('Average Length of Stay')}
                        </h3>
                        <small className='label-xsmall'>
                            {t('The Average Length of Stay (ALOS) measures how many nights guests typically stay at your property.')}
                        </small>
                        <a className='label-xsmall mt-2' href={`${langPrefix}/alos-calculator/`}>
                            {t('View')} &gt;
                        </a>
                    </div>
                    <div className='rounded-white-box box d-flex flex-column'>
                        <h3 style={{ fontSize: 20 }}>
                            {t('Linen PAR')}
                        </h3>
                        <small className='label-xsmall'>
                            {t('Calculate the ideal hotel linen PAR (inventory) to optimize costs and ensure guest satisfaction.')}
                        </small>
                        <a className='label-xsmall mt-2' href={`${langPrefix}/linen-par-calculator/`}>
                            {t('View')} &gt;
                        </a>
                    </div>
                    <div className='rounded-white-box box d-flex flex-column'>
                        <h3 style={{ fontSize: 20 }}>
                            {t('Customer Acquisition Cost')}
                        </h3>
                        <small className='label-xsmall'>
                            {t('Customer Acquisition Cost (CAC) calculates the average marketing spend to acquire a new guest.')}
                        </small>
                        <a className='label-xsmall mt-2' href={`${langPrefix}/customer-acquisition-cost-cac-calculator/`}>
                            {t('View')} &gt;
                        </a>
                    </div>
                </div>
                <div className='rounded-white-box d-flex flex-xl-row flex-column box-width' style={{ boxShadow: '0px 64px 128px #E3E9FC', borderRadius: '8px' }}>
                    <div className='box-padding gap-2 d-flex flex-column'>
                        <h3>
                            {t('Smarter Hotel Management with Real‑Time ADR, RevPAR & Occupancy Calculators')}
                        </h3>
                        <p>
                            {t('From automated rate management and real‑time reporting to integrated POS, smart reservations, and a full suite of online calculators for hotels, Prostay equips you with every tool required to run a data‑driven operation. Instantly generate ADR, RevPAR, Occupancy Rate, GOPPAR, and TRevPAR with our built‑in calculators—no spreadsheets or manual formulas—so you can price rooms accurately, compare performance across seasons, and forecast demand with confidence.')}
                        </p>
                        <p>
                            {t('Whether you oversee a boutique resort or a multi‑property portfolio, Prostay streamlines workflows by syncing every reservation, payment, and inventory change in one cloud dashboard. Intelligent automation slashes repetitive tasks, alerts you to revenue leaks, and recommends dynamic pricing adjustments that raise both RevPAR and bottom‑line profit. In short, our platform turns raw hotel data into actionable insights, freeing your team to focus on delivering exceptional guest experiences while maximising profitability.')}
                        </p>
                        <div className='active-button'>
                            <a className='light-blue-filled-btn-xsmall' style={{ height: '38px', width: '124px', color: '#fff' }} href={`${langPrefix}/property-management-system/`}>
                                {t('Learn More')}
                                <ArrowRight />
                            </a>
                        </div>

                    </div>
                    <div className='box-padding'>
                        <img src='/images/tools-dashboard.svg' alt='Property Management System analytics' />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ToolsDashboard;